import apiClient from './axios';
import { AxiosError } from 'axios';
import type {
  AuthUser,
  AuthUserRole,
  AuthTokens,
  AuthResponse,
  ProfileApiResponse,
  RegisterResponse,
  FreshTokenResponse,
} from '@cms/models';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
}

/** Lấy tên role để hiển thị (từ JWT claim / profile). */
export function getRoleDisplayName(role: AuthUserRole): string | null {
  if (role == null) return null;
  return typeof role === 'string' ? role : (role.name ?? null);
}

export type RefreshResponse = AuthTokens;

/**
 * Normalize profile API response to AuthUser.
 * Handles backend returning role as string and optional fields (displayName, etc.).
 */
export function parseProfileResponse(raw: unknown): AuthUser {
  const p = raw as ProfileApiResponse | null | undefined;
  if (!p || typeof p !== 'object' || !String(p.id) || !String(p.email)) {
    throw new Error('Invalid profile response');
  }
  let role: AuthUserRole = null;
  if (p.role != null && p.role !== '') {
    if (typeof p.role === 'string') {
      role = p.role;
    } else if (
      typeof p.role === 'object' &&
      p.role !== null &&
      typeof (p.role as { name?: string }).name === 'string'
    ) {
      role = p.role as { id: string; name: string; scopes: string[] };
    }
  }
  const roleNameStr = typeof p.role === 'string' ? p.role : null;
  const inferredPlatformFromRole = roleNameStr === 'Super Admin';

  return {
    id: String(p.id),
    email: String(p.email),
    name: p.name ?? p.displayName ?? null,
    avatarUrl: p.avatarUrl ?? null,
    emailVerified: Boolean(p.emailVerified),
    role,
    isPlatformUser: Boolean(p.isPlatformUser) || inferredPlatformFromRole,
    isNewUser: undefined,
  };
}

export interface ParsedError {
  code: string;
  messageKey: string;
  message: string;
}

const TOKEN_KEYS = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
  EXPIRES: 'token_expires',
};

export interface AuthApi {
  login: (email: string, password: string) => Promise<AuthResponse>;
  register: (data: RegisterRequest) => Promise<RegisterResponse>;
  refreshToken: () => Promise<boolean>;
  /**
   * Get a fresh access token without rotating the refresh token.
   * Useful for proactive/background token refresh and multi-tab scenarios.
   */
  getFreshToken: () => Promise<boolean>;
  logout: () => Promise<void>;
  getProfile: () => Promise<AuthUser>;
  changePassword: (payload: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  storeTokens: (tokens: AuthTokens) => void;
  clearTokens: () => void;
  isAuthenticated: () => boolean;
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  /** Expiry timestamp (ms). Null nếu không có token/expires. */
  getAccessTokenExpiresAt: () => number | null;
  /** True nếu access token hết hạn trong vòng `seconds` giây. */
  isAccessTokenExpiringWithin: (seconds: number) => boolean;
  /** Scopes từ JWT payload (dùng khi chưa load profile). */
  getScopesFromToken: () => string[];
  /** Tenant id từ JWT payload (multi-tenant SaaS). */
  getTenantIdFromToken: () => string | null;
  /** Thông tin user tối thiểu từ JWT payload (để hiển thị trước khi load profile). */
  getUserFromToken: () => AuthUser | null;
}

function storeTokens(tokens: AuthTokens): void {
  const expiresAt = Date.now() + tokens.expiresIn * 1000;
  localStorage.setItem(TOKEN_KEYS.ACCESS, tokens.accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH, tokens.refreshToken);
  localStorage.setItem(TOKEN_KEYS.EXPIRES, expiresAt.toString());
  // Backward compatibility for existing code
  localStorage.setItem('token', tokens.accessToken);
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEYS.ACCESS);
  localStorage.removeItem(TOKEN_KEYS.REFRESH);
  localStorage.removeItem(TOKEN_KEYS.EXPIRES);
  localStorage.removeItem('token');
}

function isAuthenticated(): boolean {
  const token = localStorage.getItem(TOKEN_KEYS.ACCESS);
  const expires = localStorage.getItem(TOKEN_KEYS.EXPIRES);
  if (!token || !expires) return false;
  return Date.now() < Number.parseInt(expires, 10);
}

function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.ACCESS);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.REFRESH);
}

function getAccessTokenExpiresAt(): number | null {
  const raw = localStorage.getItem(TOKEN_KEYS.EXPIRES);
  if (!raw) return null;
  const expires = Number.parseInt(raw, 10);
  return Number.isNaN(expires) ? null : expires;
}

function isAccessTokenExpiringWithin(seconds: number): boolean {
  const expiresAt = getAccessTokenExpiresAt();
  if (expiresAt == null) return true;
  const threshold = Date.now() + seconds * 1000;
  return expiresAt <= threshold;
}

/** JWT payload từ backend: sub, email, role (string), scopes (string[]), tenantId (string). */
export interface TokenPayload {
  sub?: string;
  email?: string;
  role?: string;
  roles?: string[];
  scopes?: string[];
  tenantId?: string;
  isPlatformUser?: boolean;
  iat?: number;
  exp?: number;
}

/**
 * Decode JWT payload (chỉ decode, không verify; API sẽ verify).
 * Trả về payload hoặc null nếu token invalid.
 */
export function decodeTokenPayload(token: string | null): TokenPayload | null {
  const base64 = token?.split('.')[1];
  if (!base64) return null;
  try {
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as TokenPayload;
  } catch {
    return null;
  }
}

/**
 * Lấy thông tin user tối thiểu từ JWT payload (để hiển thị ngay khi có token, trước khi GET /auth/profile).
 * Backend chỉ gửi sub, email, role, scopes trong token; name/avatarUrl luôn null.
 */
export function getUserFromToken(): AuthUser | null {
  const token = getAccessToken();
  const payload = decodeTokenPayload(token);
  if (payload?.sub == null) return null;
  const roleName = payload.role ?? (Array.isArray(payload.roles) ? payload.roles[0] : undefined);
  const scopes = Array.isArray(payload.scopes) ? payload.scopes : [];
  const hasGlobalScope = scopes.some(s => s === '*:*' || s === '*');
  let role: AuthUserRole;
  if (roleName != null) {
    role = { id: '', name: roleName, scopes };
  } else if (scopes.length > 0) {
    role = { id: '', name: '', scopes };
  } else {
    role = null;
  }
  return {
    id: payload.sub,
    email: payload.email ?? '',
    name: null,
    avatarUrl: null,
    emailVerified: false,
    role,
    isPlatformUser: Boolean(payload.isPlatformUser) || hasGlobalScope,
    // tenantId is optional on AuthUser; populated when present in JWT
    tenantId: payload.tenantId ?? null,
  };
}

/**
 * Lấy danh sách scopes từ access token (source of truth khi chưa có user từ profile).
 */
function getScopesFromToken(): string[] {
  const token = getAccessToken();
  const payload = decodeTokenPayload(token);
  if (!payload?.scopes || !Array.isArray(payload.scopes)) return [];
  return payload.scopes;
}

function getTenantIdFromToken(): string | null {
  const token = getAccessToken();
  const payload = decodeTokenPayload(token);
  return payload?.tenantId ?? null;
}

/**
 * Parse axios error response to extract error code, messageKey, and message.
 * Handles network errors, timeout errors, and missing responses.
 */
function parseError(error: AxiosError): ParsedError {
  const response = error.response;

  // Handle response with error data
  if (response?.data) {
    const errorData = response.data as {
      code?: string;
      messageKey?: string;
      message?: string | string[];
    };

    // Extract message from errorData, handling string, array, or undefined cases
    let extractedMessage: string;
    if (typeof errorData.message === 'string') {
      extractedMessage = errorData.message;
    } else if (Array.isArray(errorData.message) && errorData.message.length > 0) {
      extractedMessage = errorData.message[0];
    } else {
      extractedMessage = error.message || 'An unexpected error occurred';
    }

    return {
      code: errorData.code || 'UNKNOWN_ERROR',
      messageKey: errorData.messageKey || 'auth.errors.generic',
      message: extractedMessage,
    };
  }

  // Network errors (no response)
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return {
      code: 'NETWORK_ERROR',
      messageKey: 'auth.errors.network_error',
      message: 'Network request timed out',
    };
  }

  if (error.message.includes('Network Error') || error.code === 'ERR_NETWORK') {
    return {
      code: 'NETWORK_ERROR',
      messageKey: 'auth.errors.network_error',
      message: 'Network request failed. Please check your connection.',
    };
  }

  // Connection failed errors
  if (error.code === 'ECONNREFUSED' || error.message.includes('connection')) {
    return {
      code: 'NETWORK_ERROR',
      messageKey: 'auth.errors.network_error',
      message: 'Unable to connect to server',
    };
  }

  // Fallback for missing response
  return {
    code: 'UNKNOWN_ERROR',
    messageKey: 'auth.errors.generic',
    message: error.message || 'An unexpected error occurred',
  };
}

const realAuthApi: AuthApi = {
  async login(email: string, password: string): Promise<AuthResponse> {
    try {
      const { data } = await apiClient.post<AuthResponse>('/auth/login', {
        email,
        password,
      });
      storeTokens({
        accessToken: data.accessToken,
        // Backend may or may not return refreshToken / expiresIn; fall back safely.
        refreshToken: data.refreshToken ?? data.accessToken,
        expiresIn: data.expiresIn ?? 3600,
      });
      return data;
    } catch (error) {
      const parsedError = parseError(error as AxiosError);
      throw parsedError;
    }
  },

  async register(data: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await apiClient.post<RegisterResponse>('/auth/register', data);
      return response.data;
    } catch (error) {
      const parsedError = parseError(error as AxiosError);
      throw parsedError;
    }
  },

  async refreshToken(): Promise<boolean> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const { data } = await apiClient.post<RefreshResponse>('/auth/refresh', {
        refreshToken,
      });
      storeTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: data.expiresIn,
      });
      return true;
    } catch (error) {
      // Parse error for potential logging/debugging, but maintain boolean return contract
      const parsedError = parseError(error as AxiosError);
      clearTokens();
      // Log parsed error for debugging while maintaining API contract
      console.debug('Token refresh failed:', parsedError);
      return false;
    }
  },

  async getFreshToken(): Promise<boolean> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const { data } = await apiClient.post<FreshTokenResponse>('/auth/fresh-token', {
        refreshToken,
      });
      // Update only access token and expiry, keep the same refresh token
      if (!data.accessToken) {
        throw new TypeError('Fresh token response missing accessToken');
      }
      if (typeof data.expiresIn !== 'number') {
        throw new TypeError('Fresh token response missing expiresIn');
      }
      const expiresAt = Date.now() + data.expiresIn * 1000;
      localStorage.setItem(TOKEN_KEYS.ACCESS, data.accessToken);
      localStorage.setItem(TOKEN_KEYS.EXPIRES, expiresAt.toString());
      localStorage.setItem('token', data.accessToken);
      return true;
    } catch (error) {
      const parsedError = parseError(error as AxiosError);
      console.debug('Fresh token failed:', parsedError);
      // Don't clear tokens - let the full refresh handle that
      return false;
    }
  },

  async logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        await apiClient.post('/auth/logout', { refreshToken });
      } catch (error) {
        // Parse error but don't throw - logout should always succeed locally
        const parsedError = parseError(error as AxiosError);
        // Log error for debugging but continue with local cleanup
        console.warn('Logout API call failed:', parsedError);
      }
    }
    clearTokens();
  },

  async getProfile(): Promise<AuthUser> {
    const { data } = await apiClient.get<ProfileApiResponse>('/auth/profile');
    return parseProfileResponse(data);
  },

  async changePassword(payload: {
    currentPassword: string;
    newPassword: string;
  }): Promise<void> {
    try {
      await apiClient.post('/auth/password/change', payload);
    } catch (error) {
      throw parseError(error as AxiosError);
    }
  },

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiClient.post('/auth/password/reset', { email });
    } catch (error) {
      const parsedError = parseError(error as AxiosError);
      throw parsedError;
    }
  },

  async resendVerificationEmail(email: string): Promise<void> {
    try {
      await apiClient.post('/auth/email/verify', { email });
    } catch (error) {
      const parsedError = parseError(error as AxiosError);
      throw parsedError;
    }
  },

  storeTokens,
  clearTokens,
  isAuthenticated,
  getAccessToken,
  getRefreshToken,
  getAccessTokenExpiresAt,
  isAccessTokenExpiringWithin,
  getScopesFromToken,
  getTenantIdFromToken,
  getUserFromToken,

  // getFreshToken is implemented above
};

export const authApi: AuthApi = realAuthApi;
